import { useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Button,
  Combobox,
  Link,
  Modal,
  ModalBody,
  ModalFooter,
  Paragraph,
  Stack,
  TextField,
  useSnackbars,
  type PlumaManagedModalProps,
} from '@customerio/pluma-components/react';
import { usePermissions } from '@customerio/hydra-permissions/react';
import { searchManualSegmentsQueryOptions } from '@/services/db/segment/queries';
import { useAddCustomersToManualSegment, useCreateSegment } from '@/services/db/segment/mutations';
import { useProductTaxonomy } from '@/services/product-taxonomy';
import { useDebouncedValue } from '@/hooks/state/use-debounced-value';
import { sentryCaptureException } from '@/utils/sentry';

const SEARCH_DEBOUNCE_MS = 500;

export interface AddPeopleToManualSegmentModalData {
  workspaceId: number;
  /** Selected profile `cio_id`s (the membership endpoint's `customer_id`). */
  customerIds: readonly string[];
  /** "N selected people" — composed by the actions bar. */
  peopleCountLabel: string;
}

interface SegmentOption {
  value: string;
  label: string;
}

interface AddOutcome {
  total: number;
  failed: number;
}

interface ReportAddOutcomeDeps {
  snackbars: ReturnType<typeof useSnackbars>;
  peoplePlural: string;
  peopleCountLabel: string;
  segment: SegmentOption;
  outcome: AddOutcome;
  onConfirm: () => void;
}

/**
 * The outcome snackbar: success and partial success confirm the modal (the
 * caller clears the selection and reloads); total failure leaves it open for
 * a retry.
 */
function reportAddOutcome({
  snackbars,
  peoplePlural,
  peopleCountLabel,
  segment,
  outcome,
  onConfirm,
}: ReportAddOutcomeDeps) {
  const { total, failed } = outcome;
  const succeeded = total - failed;

  if (failed > 0) {
    sentryCaptureException(new Error('addToManualSegment failed for some profiles'), {
      extra: { segmentId: segment.value, totalCount: total, failureCount: failed },
    });
  }

  if (failed === 0) {
    snackbars.create({
      variant: 'default',
      id: 'people.list.add-to-manual-segment.success',
      message: `${peopleCountLabel} will be added to "${segment.label}" soon`,
    });
    onConfirm();
  } else if (succeeded === 0) {
    snackbars.create({
      variant: 'critical',
      id: 'people.list.add-to-manual-segment.failure',
      message: `Unable to add ${peoplePlural} to "${segment.label}". Please try again or contact support.`,
    });
  } else {
    snackbars.create({
      variant: 'critical',
      id: 'people.list.add-to-manual-segment.partial',
      message: `Added ${succeeded} of ${total} ${peoplePlural} to "${segment.label}". ${failed} failed — please try again for the rest.`,
    });
    onConfirm();
  }
}

type SelectSegmentBodyProps = {
  peopleCountLabel: string;
  inputValue: string;
  onInputValueChange: (value: string) => void;
  selectedSegmentId: string | null;
  onSelectionChange: (value: string | null) => void;
  options: SegmentOption[];
  isLoading: boolean;
  canCreateSegments: boolean;
  onSwitchToCreate: () => void;
};

function SelectSegmentBody({
  peopleCountLabel,
  inputValue,
  onInputValueChange,
  selectedSegmentId,
  onSelectionChange,
  options,
  isLoading,
  canCreateSegments,
  onSwitchToCreate,
}: SelectSegmentBodyProps) {
  return (
    <Stack gap="150">
      <Paragraph>Select a manual segment to add {peopleCountLabel} to.</Paragraph>

      <Combobox
        data-test-add-to-manual-segment-combobox
        ariaLabel="Select a manual segment"
        placeholder="Search manual segments…"
        inputValue={inputValue}
        onValueChange={onInputValueChange}
        selectedValue={selectedSegmentId}
        onSelectionChange={onSelectionChange}
        options={options}
        isLoading={isLoading}
        shouldFilter={false}
      />

      {canCreateSegments ? (
        <Link
          data-test-add-to-manual-segment-create-toggle
          variant="secondary"
          icon="add"
          isUnderlined
          onClick={onSwitchToCreate}
        >
          Create a new manual segment
        </Link>
      ) : null}
    </Stack>
  );
}

type CreateSegmentBodyProps = {
  peopleCountLabel: string;
  newSegmentName: string;
  onNewSegmentNameChange: (value: string) => void;
  isRunning: boolean;
  onSwitchToSelect: () => void;
};

function CreateSegmentBody({
  peopleCountLabel,
  newSegmentName,
  onNewSegmentNameChange,
  isRunning,
  onSwitchToSelect,
}: CreateSegmentBodyProps) {
  return (
    <Stack gap="150">
      <Paragraph>Create a new manual segment and add {peopleCountLabel} to it.</Paragraph>

      <TextField
        data-test-add-to-manual-segment-new-name
        ariaLabel="New segment name"
        value={newSegmentName}
        onInput={(event) => onNewSegmentNameChange(event.target.value)}
        placeholder="e.g. VIP customers"
        isDisabled={isRunning}
      />

      <Link
        data-test-add-to-manual-segment-select-toggle
        variant="secondary"
        icon="arrow-left"
        isUnderlined
        onClick={onSwitchToSelect}
      >
        Select an existing segment instead
      </Link>
    </Stack>
  );
}

function confirmButtonLabel(mode: 'select' | 'create'): string {
  return mode === 'create' ? 'Create segment & add' : 'Add to segment';
}

/**
 * Server-side segment search with the picker's controlled state. A cleared
 * input searches immediately; typing debounces. A just-created segment stays
 * in the options even when the (re-run) search doesn't return it yet. The
 * picked segment is held as a full option (id + label), so confirming never
 * depends on the in-flight search results still containing it.
 */
function useManualSegmentPicker(workspaceId: number) {
  const [selectedOption, setSelectedOption] = useState<SegmentOption | null>(null);
  const [inputValue, setInputValue] = useState('');
  const [createdOption, setCreatedOption] = useState<SegmentOption | null>(null);

  const [debouncedInput] = useDebouncedValue(inputValue, SEARCH_DEBOUNCE_MS);
  const searchTerm = inputValue === '' ? '' : debouncedInput;
  const optionsQuery = useQuery(searchManualSegmentsQueryOptions(workspaceId, searchTerm));

  const options: SegmentOption[] = useMemo(() => {
    const fetched = (optionsQuery.data ?? []).map((segment) => ({ value: segment.id, label: segment.name }));
    if (createdOption == null) return fetched;
    return [createdOption, ...fetched.filter((option) => option.value !== createdOption.value)];
  }, [optionsQuery.data, createdOption]);

  const handleSelectionChange = (value: string | null) => {
    const match = value == null ? null : (options.find((option) => option.value === value) ?? null);
    setSelectedOption(match);
    setInputValue(match?.label ?? '');
  };

  const selectSegment = (option: SegmentOption) => {
    setCreatedOption(option);
    setSelectedOption(option);
    setInputValue(option.label);
  };

  return {
    selectedOption,
    inputValue,
    setInputValue,
    options,
    isLoading: optionsQuery.isFetching || searchTerm !== inputValue,
    handleSelectionChange,
    selectSegment,
  };
}

/**
 * Adds the selected profiles to a manual segment — an existing one picked via
 * server-side search, or a new one created inline (permission-gated). After a
 * create, the modal switches back to select mode with the new segment picked,
 * so retrying a failed membership fan-out can't create a duplicate segment.
 */
export const AddPeopleToManualSegmentModal = ({
  data,
  onConfirm,
  onClose,
  modalState,
}: PlumaManagedModalProps<AddPeopleToManualSegmentModalData>) => {
  const { workspaceId, customerIds, peopleCountLabel } = data;
  const taxonomy = useProductTaxonomy();
  const snackbars = useSnackbars();
  const { hasPermission } = usePermissions();
  const canCreateSegments = hasPermission('journeys/segments/create');

  const [mode, setMode] = useState<'select' | 'create'>('select');
  const [newSegmentName, setNewSegmentName] = useState('');
  const picker = useManualSegmentPicker(workspaceId);

  const createSegment = useCreateSegment({ workspaceId });
  const addCustomers = useAddCustomersToManualSegment({ workspaceId });

  const isRunningRef = useRef(false);
  const [isRunning, setIsRunning] = useState(false);

  const trimmedNewSegmentName = newSegmentName.trim();
  const isConfirmDisabled = mode === 'create' ? trimmedNewSegmentName.length === 0 : picker.selectedOption == null;

  // Returns the created segment as an option, or null after reporting the
  // failure (the modal stays in create mode for a retry).
  const createNewSegment = async (): Promise<SegmentOption | null> => {
    try {
      const created = await createSegment.mutateAsync({ name: trimmedNewSegmentName, type: 'static' });
      const option = { value: created.id, label: created.name };
      // Select mode with the new segment picked, so a retry after a
      // partial/total membership failure doesn't create a second segment.
      picker.selectSegment(option);
      setMode('select');
      return option;
    } catch (error) {
      sentryCaptureException(error instanceof Error ? error : new Error(String(error)));
      snackbars.create({
        variant: 'critical',
        id: 'people.list.add-to-manual-segment.create-failed',
        message: 'Unable to create the new segment. Please try again.',
      });
      return null;
    }
  };

  const resolveTargetSegment = async (): Promise<SegmentOption | null> => {
    if (mode === 'create') {
      if (trimmedNewSegmentName.length === 0 || !canCreateSegments) return null;
      return createNewSegment();
    }
    return picker.selectedOption;
  };

  const confirm = async () => {
    const segment = await resolveTargetSegment();
    if (segment == null) return;
    const outcome = await addCustomers.mutateAsync({ segmentId: segment.value, customerIds });
    reportAddOutcome({
      snackbars,
      peoplePlural: taxonomy.term('people.plural'),
      peopleCountLabel,
      segment,
      outcome,
      onConfirm,
    });
  };

  const handleConfirmClick = () => {
    if (isRunningRef.current) return;
    isRunningRef.current = true;
    setIsRunning(true);
    void confirm().finally(() => {
      isRunningRef.current = false;
      setIsRunning(false);
    });
  };

  return (
    <Modal state={modalState} title="Add to manual segment" onClose={onClose} isDismissable={!isRunning}>
      <ModalBody>
        {mode === 'select' ? (
          <SelectSegmentBody
            peopleCountLabel={peopleCountLabel}
            inputValue={picker.inputValue}
            onInputValueChange={picker.setInputValue}
            selectedSegmentId={picker.selectedOption?.value ?? null}
            onSelectionChange={picker.handleSelectionChange}
            options={picker.options}
            isLoading={picker.isLoading}
            canCreateSegments={canCreateSegments}
            onSwitchToCreate={() => {
              setMode('create');
              setNewSegmentName(picker.inputValue);
            }}
          />
        ) : (
          <CreateSegmentBody
            peopleCountLabel={peopleCountLabel}
            newSegmentName={newSegmentName}
            onNewSegmentNameChange={setNewSegmentName}
            isRunning={isRunning}
            onSwitchToSelect={() => setMode('select')}
          />
        )}
      </ModalBody>

      <ModalFooter>
        <Button variant="tertiary" onClick={() => onClose()} isDisabled={isRunning}>
          Cancel
        </Button>
        <Button
          data-test-add-to-manual-segment-confirm
          variant="primary"
          isDisabled={isConfirmDisabled}
          isLoading={isRunning}
          onClick={handleConfirmClick}
        >
          {confirmButtonLabel(mode)}
        </Button>
      </ModalFooter>
    </Modal>
  );
};
